import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import { getCompanySelf, updateCompanySelf, uploadCompanyLogo } from '../../services/company';
import { onMutateSuccess, onQuerySuccess } from '../../utils/queryResponse';
import AvatarUpload from '../../components/avatarUpload';
import CountrySelect from '../../components/selects/countrySelect';
import OverlayLoader from '../../components/overlayLoader';
import PhoneInput, { getPhoneCountryOptions } from '../../components/forms/phoneInput';

const pageStyles = `
  .sbr .panel{background:#fff;border:1px solid var(--line);border-radius:var(--r);box-shadow:var(--sh-2);overflow:hidden;position:relative}
  .sbr .panel .phd{display:flex;align-items:center;gap:14px;padding:20px 24px;border-bottom:1px solid var(--line);background:linear-gradient(180deg,#FBFCFF,#fff)}
  .sbr .panel .phd .ti{font-size:17px;font-weight:800;color:var(--ink);letter-spacing:-.3px}
  .sbr .panel .phd .ds{font-size:12.5px;color:var(--muted);font-weight:500;margin-top:2px}
  .sbr .pbody{padding:24px}
  .sbr .avrow{display:flex;align-items:center;gap:18px;padding-bottom:22px;margin-bottom:22px;border-bottom:1px solid var(--line-soft)}
  .sbr .avmeta .nm{font-size:17px;font-weight:800;color:var(--ink);letter-spacing:-.3px}
  .sbr .avmeta .hint{font-size:12px;color:var(--muted);font-weight:500;margin-top:4px}
  .sbr .seclbl{font-size:11px;font-weight:800;letter-spacing:.6px;text-transform:uppercase;color:#9AA4B2;margin:22px 0 14px}
  .sbr .seclbl:first-child{margin-top:0}
  .sbr .fgrid{display:grid;grid-template-columns:1fr 1fr;gap:18px 22px}
  @media(max-width:700px){.sbr .fgrid{grid-template-columns:1fr}}
  .sbr .field{display:flex;flex-direction:column;gap:7px}
  .sbr .field.full{grid-column:1 / -1}
  .sbr .field label{font-size:12.5px;font-weight:700;color:var(--text)}
  .sbr .inp{display:flex;align-items:center;gap:9px;background:#fff;border:1px solid var(--line);border-radius:var(--r-sm);
    padding:11px 13px;font-size:14px;font-weight:600;color:var(--ink);box-shadow:var(--sh-1);transition:.14s}
  .sbr .inp:focus-within{border-color:var(--brand);box-shadow:0 0 0 3px var(--brand-50)}
  .sbr .inp input{border:0;outline:0;background:transparent;flex:1;font-family:inherit;font-size:14px;font-weight:600;color:var(--ink);min-width:0}
  .sbr .inp input::placeholder{color:var(--faint);font-weight:500}
  .sbr .savebar{display:flex;align-items:center;gap:12px;margin-top:24px;padding-top:20px;border-top:1px solid var(--line)}
  .sbr .savebar .sp{margin-left:auto;display:flex;gap:10px}
  @media(max-width:560px){
    .sbr .avrow{flex-wrap:wrap}
    .sbr .savebar{flex-wrap:wrap;gap:14px}
    .sbr .savebar .sp{margin-left:0;width:100%}
    .sbr .savebar .sp .btn-sbr{flex:1;justify-content:center}
  }
`;

const initCompany = {
    name: '',
    logoUrl: '',
    phone: '',
    email: '',
    street: '',
    city: '',
    state: '',
    zipCode: '',
    country: '',
    registrationNumber: '',
    taxNumber: '',
    bankAccount: '',
    phoneCountryCode: '',
    phoneCountry: 'RO',
    invoiceMessageTemplate: '',
    receiptMessageTemplate: '',
    workOrderMessageTemplate: '',
    invoicePrefix: 'INV',
    paymentTermsDays: 14,
    trackingEnabled: false,
    reviewRequestEnabled: false,
    googleReviewUrl: '',
    trackingMessages: { confirmed: '', scheduled: '', ready: '', completed: '' },
    materialsEnabled: false,
}

const BOOLEAN_FIELDS = ['trackingEnabled', 'reviewRequestEnabled', 'materialsEnabled']
const TRACKING_PLACEHOLDERS = ['{clientName}', '{jobNumber}', '{date}', '{link}']
const DOCUMENT_PLACEHOLDERS = ['{clientName}', '{documentNumber}', '{jobNumber}', '{link}']

const INVOICE_MESSAGE_PLACEHOLDERS = ['{clientName}', '{invoiceNumber}', '{jobNumber}', '{link}'];

// null -> '' so inputs stay controlled
const normalize = (obj) => {
    const next = { ...initCompany }
    for (const key in next) {
        if (BOOLEAN_FIELDS.includes(key)) next[key] = !!obj?.[key]
        else if (key === 'trackingMessages') next[key] = { ...initCompany.trackingMessages, ...(obj?.trackingMessages || {}) }
        else if (key === 'paymentTermsDays') next[key] = obj?.paymentTermsDays ?? 14
        else next[key] = obj?.[key] ?? ''
    }
    return next
}

const CompanyProfilePage = () => {

    const { t } = useTranslation();
    // `placeholder` shows the built-in default message that is sent when the
    // field is left empty (same default useCompanyMessaging falls back to).
    const TRACKING_STAGES = [
        { key: 'confirmed', label: t('settingsPage.confirmed'), hint: t('settingsPage.trackingConfirmedHint'), placeholder: t('defaultMessages.trackingConfirmed') },
        { key: 'scheduled', label: t('settingsPage.scheduled'), hint: t('settingsPage.trackingScheduledHint'), placeholder: t('defaultMessages.trackingScheduled') },
        { key: 'ready', label: t('settingsPage.ready'), hint: t('settingsPage.trackingReadyHint'), placeholder: t('defaultMessages.trackingReady') },
        { key: 'completed', label: t('settingsPage.completed'), hint: t('settingsPage.trackingCompletedHint'), placeholder: t('defaultMessages.trackingCompleted') },
    ]

    const [company, setCompany] = useState(initCompany)
    const queryClient = useQueryClient()

    const { data, isFetching } = useQuery(['company', 'self'], getCompanySelf)

    const { mutate, isLoading } = useMutation(updateCompanySelf, {
        onSuccess: onMutateSuccess(() => {
            queryClient.invalidateQueries(['company'])
        }, t('settingsPage.companyProfileSaved'))
    })

    useEffect(() => {
        onQuerySuccess(data, (result) => setCompany(normalize(result)))
    }, [data])

    const handleChange = (e) => setCompany({ ...company, [e.target.name]: e.target.value })
    const handleToggle = (name) => setCompany({ ...company, [name]: !company[name] })
    const handleTrackingMessage = (key, value) => setCompany({ ...company, trackingMessages: { ...company.trackingMessages, [key]: value } })

    const textareaStyle = { border: 0, outline: 0, background: 'transparent', flex: 1, fontFamily: 'inherit', fontSize: 14, fontWeight: 500, color: 'var(--ink)', resize: 'vertical', minWidth: 0 }
    const Placeholders = ({ list }) => <>{list.map((p, i) => <span key={p}><code>{p}</code>{i < list.length - 1 ? ', ' : ''}</span>)}</>

    const handleSubmit = (e) => {
        e.preventDefault()
        const { logoUrl, ...payload } = company // logo is saved via its own endpoint
        // An empty dial code means "keep the current one" — the API rejects ''.
        if (!payload.phoneCountryCode) delete payload.phoneCountryCode
        if (!payload.phoneCountry) delete payload.phoneCountry
        if (!payload.invoicePrefix) delete payload.invoicePrefix
        payload.paymentTermsDays = Number(payload.paymentTermsDays) || 0
        mutate(payload)
    }

    const handleLogo = async (file) => {
        const res = await uploadCompanyLogo(file)
        if (res?.code === 200 && res.message?.logoUrl) {
            setCompany((c) => ({ ...c, logoUrl: res.message.logoUrl }))
            queryClient.invalidateQueries(['company'])
        } else {
            throw new Error(t('avatarUpload.uploadFailed'))
        }
    }

    const reset = () => onQuerySuccess(data, (result) => setCompany(normalize(result)))

    return (
        <div className="sbr">
            <style>{pageStyles}</style>

            {/* Desktop header */}
            <div className="dhead sbr-d">
                <div>
                    <div className="ttl">{t('settingsPage.companyProfile')}</div>
                    <div className="sub">{t('settingsPage.shownOnInvoices')}</div>
                </div>
            </div>

            {/* Mobile header */}
            <div className="sbr-m" style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-.8px', color: 'var(--ink)' }}>{t('settingsPage.companyProfile')}</div>
                <div style={{ fontSize: 13, color: 'var(--muted)', fontWeight: 500, marginTop: 2 }}>{t('settingsPage.shownOnInvoicesShort')}</div>
            </div>

            <section className="panel">
                <div className="phd">
                    <div>
                        <div className="ti">{t('settingsPage.companyDetailsTitle')}</div>
                        <div className="ds">{t('settingsPage.companyDetailsSubtitle')}</div>
                    </div>
                </div>

                <div className="pbody">
                    {/* Logo block */}
                    <div className="avrow">
                        <AvatarUpload square size={72} name={company.name} src={company.logoUrl}
                            onUpload={handleLogo}
                            uploadLabel={t('settingsPage.uploadLogo')} changeLabel={t('settingsPage.changeLogo')}
                        />
                        <div className="avmeta">
                            <div className="nm">{company.name || '—'}</div>
                            <div className="hint">{t('settingsPage.logoHint')}</div>
                        </div>
                    </div>

                    <form onSubmit={handleSubmit}>
                        <div className="seclbl">{t('settingsPage.company')}</div>
                        <div className="fgrid">
                            <div className="field full">
                                <label>{t('settingsPage.companyName')} <span className="text-danger">*</span></label>
                                <div className="inp">
                                    <input type="text" name="name" value={company.name} onChange={handleChange} placeholder={t('settingsPage.companyName')} required />
                                </div>
                            </div>
                            <div className="field">
                                <label>{t('settingsPage.phone')}</label>
                                <PhoneInput
                                    name="phone"
                                    value={company.phone ?? ''}
                                    onChange={(phone) => setCompany((c) => ({ ...c, phone }))}
                                    defaultCountry={company.phoneCountry || 'RO'}
                                    placeholder={t('settingsPage.phone')}
                                />
                            </div>
                            <div className="field">
                                <label>{t('settingsPage.email')}</label>
                                <div className="inp">
                                    <input type="email" name="email" value={company.email} onChange={handleChange} placeholder="company@example.com" />
                                </div>
                            </div>
                        </div>

                        <div className="seclbl">{t('settingsPage.address')}</div>
                        <div className="fgrid">
                            <div className="field full">
                                <label>{t('settingsPage.street')}</label>
                                <div className="inp">
                                    <input type="text" name="street" value={company.street} onChange={handleChange} placeholder={t('settingsPage.streetPlaceholder')} />
                                </div>
                            </div>
                            <div className="field">
                                <label>{t('settingsPage.city')}</label>
                                <div className="inp">
                                    <input type="text" name="city" value={company.city} onChange={handleChange} placeholder={t('settingsPage.city')} />
                                </div>
                            </div>
                            <div className="field">
                                <label>{t('settingsPage.zip')}</label>
                                <div className="inp">
                                    <input type="text" name="zipCode" value={company.zipCode} onChange={handleChange} placeholder={t('settingsPage.zipPlaceholder')} />
                                </div>
                            </div>
                            <div className="field">
                                <label>{t('settingsPage.state')}</label>
                                <div className="inp">
                                    <input type="text" name="state" value={company.state} onChange={handleChange} placeholder={t('settingsPage.statePlaceholder')} />
                                </div>
                            </div>
                            <div className="field">
                                <CountrySelect label={t('settingsPage.country')} value={company.country}
                                    onChange={(country) => setCompany({ ...company, country: country ?? '' })}
                                    placeholder={t('settingsPage.selectCountry')}
                                />
                            </div>
                        </div>

                        <div className="seclbl">{t('settingsPage.legalAndBanking')}</div>
                        <div className="fgrid">
                            <div className="field">
                                <label>{t('settingsPage.registrationNumber')}</label>
                                <div className="inp">
                                    <input type="text" name="registrationNumber" value={company.registrationNumber} onChange={handleChange} placeholder={t('settingsPage.registrationNumberPlaceholder')} />
                                </div>
                            </div>
                            <div className="field">
                                <label>{t('settingsPage.taxVatNumber')}</label>
                                <div className="inp">
                                    <input type="text" name="taxNumber" value={company.taxNumber} onChange={handleChange} placeholder={t('settingsPage.taxVatPlaceholder')} />
                                </div>
                            </div>
                            <div className="field full">
                                <label>{t('settingsPage.bankAccountIban')}</label>
                                <div className="inp">
                                    <input type="text" name="bankAccount" value={company.bankAccount} onChange={handleChange} placeholder={t('settingsPage.bankAccountPlaceholder')} />
                                </div>
                            </div>
                        </div>

                        <div className="seclbl">{t('settingsPage.messaging')}</div>
                        <div className="fgrid">
                            <div className="field">
                                <label>{t('settingsPage.defaultPhoneCountry')}</label>
                                <div className="inp">
                                    <select name="phoneCountry" value={company.phoneCountry || 'RO'} onChange={handleChange}
                                        style={{ border: 0, outline: 0, background: 'transparent', flex: 1, fontFamily: 'inherit', fontSize: 14, fontWeight: 500, color: 'var(--ink)' }}>
                                        {getPhoneCountryOptions().map((o) => (
                                            <option key={o.iso} value={o.iso}>{o.flag} {o.name} (+{o.dial})</option>
                                        ))}
                                    </select>
                                </div>
                                <small style={{ fontSize: 12, color: 'var(--muted)' }}>
                                    {t('settingsPage.defaultPhoneCountryHint')}
                                </small>
                            </div>
                            <div className="field">
                                <label>{t('settingsPage.phoneCountryCodeLegacy')}</label>
                                <div className="inp">
                                    <span style={{ color: 'var(--muted)', fontWeight: 700 }}>+</span>
                                    <input type="text" inputMode="numeric" name="phoneCountryCode" value={company.phoneCountryCode}
                                        onChange={(e) => setCompany({ ...company, phoneCountryCode: e.target.value.replace(/\D/g, '').slice(0, 4) })}
                                        placeholder="40" />
                                </div>
                                <small style={{ fontSize: 12, color: 'var(--muted)' }}>
                                    {t('settingsPage.phoneCountryCodeHint', { code: company.phoneCountryCode || '40' })}
                                </small>
                            </div>
                            <div className="field full">
                                <label>{t('settingsPage.invoiceMessage')}</label>
                                <div className="inp" style={{ alignItems: 'stretch' }}>
                                    <textarea name="invoiceMessageTemplate" value={company.invoiceMessageTemplate} onChange={handleChange} rows={6}
                                        placeholder={t('defaultMessages.invoice')}
                                        style={{ border: 0, outline: 0, background: 'transparent', flex: 1, fontFamily: 'inherit', fontSize: 14, fontWeight: 500, color: 'var(--ink)', resize: 'vertical', minWidth: 0 }} />
                                </div>
                                <small style={{ fontSize: 12, color: 'var(--muted)' }}>
                                    {t('settingsPage.invoiceMessageHint')} {t('settingsPage.placeholders')}{' '}
                                    {INVOICE_MESSAGE_PLACEHOLDERS.map((p, i) => <span key={p}><code>{p}</code>{i < INVOICE_MESSAGE_PLACEHOLDERS.length - 1 ? ', ' : ''}</span>)}.
                                    {' '}{t('settingsPage.invoiceLinkHint')}
                                </small>
                            </div>
                        </div>

                        <div className="seclbl">{t('settingsPage.invoicing')}</div>
                        <div className="fgrid">
                            <div className="field">
                                <label>{t('settingsPage.invoiceNumberPrefix')}</label>
                                <div className="inp">
                                    <input type="text" name="invoicePrefix" value={company.invoicePrefix}
                                        onChange={(e) => setCompany({ ...company, invoicePrefix: e.target.value.replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, 10) })}
                                        placeholder="INV" />
                                </div>
                                <small style={{ fontSize: 12, color: 'var(--muted)' }}>
                                    {t('settingsPage.invoicePrefixHint', { prefix: company.invoicePrefix || 'INV', year: new Date().getFullYear() })}
                                </small>
                            </div>
                            <div className="field">
                                <label>{t('settingsPage.paymentTermsLabel')}</label>
                                <div className="inp">
                                    <input type="number" min="0" max="365" name="paymentTermsDays" value={company.paymentTermsDays} onChange={handleChange} />
                                </div>
                                <small style={{ fontSize: 12, color: 'var(--muted)' }}>
                                    {t('settingsPage.paymentTermsHint')}
                                </small>
                            </div>
                        </div>

                        <div className="seclbl">{t('settingsPage.documentMessages')}</div>
                        <div className="fgrid">
                            <div className="field full">
                                <label>{t('settingsPage.pickupReceiptMessage')}</label>
                                <div className="inp" style={{ alignItems: 'stretch' }}>
                                    <textarea name="receiptMessageTemplate" value={company.receiptMessageTemplate} onChange={handleChange} rows={4}
                                        placeholder={t('defaultMessages.receipt')} style={textareaStyle} />
                                </div>
                                <small style={{ fontSize: 12, color: 'var(--muted)' }}>{t('settingsPage.pickupReceiptHint')} {t('settingsPage.placeholders')} <Placeholders list={DOCUMENT_PLACEHOLDERS} />.</small>
                            </div>
                            <div className="field full">
                                <label>{t('settingsPage.workOrderMessage')}</label>
                                <div className="inp" style={{ alignItems: 'stretch' }}>
                                    <textarea name="workOrderMessageTemplate" value={company.workOrderMessageTemplate} onChange={handleChange} rows={4}
                                        placeholder={t('defaultMessages.workOrder')} style={textareaStyle} />
                                </div>
                                <small style={{ fontSize: 12, color: 'var(--muted)' }}>{t('settingsPage.workOrderHint')} {t('settingsPage.placeholders')} <Placeholders list={DOCUMENT_PLACEHOLDERS} />.</small>
                            </div>
                        </div>

                        <div className="seclbl">{t('settingsPage.customerTracking')}</div>
                        <div className="fgrid">
                            <div className="field">
                                <label>{t('settingsPage.trackingPage')}</label>
                                <label className="inp" style={{ cursor: 'pointer' }}>
                                    <input type="checkbox" checked={company.trackingEnabled} onChange={() => handleToggle('trackingEnabled')} style={{ width: 18, height: 18, flex: 'none' }} />
                                    <span>{company.trackingEnabled ? t('settingsPage.trackingOnHint') : t('settingsPage.trackingOff')}</span>
                                </label>
                                <small style={{ fontSize: 12, color: 'var(--muted)' }}>
                                    {t('settingsPage.trackingPageHint')}
                                </small>
                            </div>
                            <div className="field">
                                <label>{t('settingsPage.googleReviewRequest')}</label>
                                <label className="inp" style={{ cursor: 'pointer' }}>
                                    <input type="checkbox" checked={company.reviewRequestEnabled} onChange={() => handleToggle('reviewRequestEnabled')} style={{ width: 18, height: 18, flex: 'none' }} />
                                    <span>{company.reviewRequestEnabled ? t('settingsPage.reviewOnHint') : t('settingsPage.trackingOff')}</span>
                                </label>
                            </div>
                            <div className="field full">
                                <label>{t('settingsPage.googleReviewLink')}</label>
                                <div className="inp">
                                    <input type="url" name="googleReviewUrl" value={company.googleReviewUrl} onChange={handleChange} placeholder="https://g.page/r/…/review" />
                                </div>
                            </div>
                            {TRACKING_STAGES.map((stage) => (
                                <div className="field full" key={stage.key}>
                                    <label>{t('settingsPage.messageAt', { stage: stage.label })}</label>
                                    <div className="inp" style={{ alignItems: 'stretch' }}>
                                        <textarea value={company.trackingMessages?.[stage.key] || ''} onChange={(e) => handleTrackingMessage(stage.key, e.target.value)} rows={3}
                                            placeholder={stage.placeholder} style={textareaStyle} />
                                    </div>
                                    <small style={{ fontSize: 12, color: 'var(--muted)' }}>{stage.hint} {t('settingsPage.placeholders')} <Placeholders list={TRACKING_PLACEHOLDERS} />.</small>
                                    {/* A saved message without {link} would reach the customer with
                                        nothing to open, and nothing else would flag it. */}
                                    {!!company.trackingMessages?.[stage.key]?.trim() && !company.trackingMessages[stage.key].includes('{link}') &&
                                        <small style={{ fontSize: 12, color: 'var(--bad, #b42318)', fontWeight: 700 }}>
                                            {t('settingsPage.trackingLinkWarning')}
                                        </small>
                                    }
                                </div>
                            ))}
                        </div>

                        <div className="seclbl">{t('settingsPage.modules')}</div>
                        <div className="fgrid">
                            <div className="field">
                                <label>{t('settingsPage.materialsParts')}</label>
                                <label className="inp" style={{ cursor: 'pointer' }}>
                                    <input type="checkbox" checked={company.materialsEnabled} onChange={() => handleToggle('materialsEnabled')} style={{ width: 18, height: 18, flex: 'none' }} />
                                    <span>{company.materialsEnabled ? t('settingsPage.materialsOnHint') : t('settingsPage.trackingOff')}</span>
                                </label>
                                <small style={{ fontSize: 12, color: 'var(--muted)' }}>
                                    {t('settingsPage.materialsHint')}
                                </small>
                            </div>
                        </div>

                        {/* Save bar */}
                        <div className="savebar">
                            <div className="sp">
                                <button type="button" className="btn-sbr btn-ghost-sbr btn-sm-sbr" onClick={reset} disabled={isLoading}>{t('common.cancel')}</button>
                                <button type="submit" className="btn-sbr btn-primary-sbr" disabled={isLoading}>
                                    {isLoading ? t('common.saving') : t('common.save')}
                                </button>
                            </div>
                        </div>
                    </form>

                    {isFetching && <OverlayLoader />}
                </div>
            </section>
        </div>
    );
};

export default CompanyProfilePage;
